import { Stack, Typography, type SxProps, type Theme, type TypographyProps } from '@mui/material';
import type { ReactNode } from 'react';

interface KeyValueProps {
    label: ReactNode;
    value: ReactNode;
    /** Styles applied to the value text when `value` is a string/number. */
    valueColor?: TypographyProps['color'];
    valueSx?: SxProps<Theme>;
    valueFontWeight?: number;
}

/**
 * Compact label + value pair on one row for mobile cards and summary layouts.
 * Pass a React node as `value` when you need custom markup.
 */
const KeyValue = ({
    label,
    value,
    valueColor = 'text.primary',
    valueSx,
    valueFontWeight = 500,
}: KeyValueProps) => {
    const isPrimitiveValue =
        typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

    return (
        <Stack
            direction="row"
            spacing={0.75}
            sx={{
                alignItems: 'baseline',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                minWidth: 0,
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {label}
            </Typography>
            {isPrimitiveValue ? (
                <Typography
                    variant="body2"
                    color={valueColor}
                    sx={{ fontWeight: valueFontWeight, ...((valueSx as object) || {}) }}
                >
                    {value}
                </Typography>
            ) : (
                value
            )}
        </Stack>
    );
};

export default KeyValue;
